'use strict';
import React from 'react';
import classNames from 'classnames';

/**
 * Two button styles which are typically used on 'button'>' elements, but can also
 * be applied to links or any other element. Buttons styles include hover, active and disabled states.
 *
 * Buttons can be disabled by setting the disabled attribute which is the easiest approach,
 * however if you're using an element which does not support a disabled attribute (e.g. anchor),
 * an is-disabled state class is also available.
 * Note that simply adding the disabled style to a link will not prevent the browser
 * from handling that link.
 *
 * @extends {React.Component}
 * @property {Boolean} disabled Renders the Button as disabled.
 * @property {String} elementType Defines whether the button is to be rendered as a <button> or <a> element.
 * @property {Boolean} secondary Renders the Button as a seconday button.
 */
class Button extends React.Component {

  /**
   * @return {PropType} Component Children.
   */
  getChildren () {
    return this.props.children;
  }

  /**
   * @return {String} Component CSS classname.
   */
  getClassName () {
    return classNames({
        'Button': true,
        'Button--secondary': this.props.secondary,
        'Button--tertiary': this.props.tertiary,
        [`${this.props.className}`]: !!this.props.className,
        'is-disabled': this.props.disabled
    });
  }

  /**
   * @return {JSX} JSX component markup.
   */
  render () {
    const { className, disabled, elementType, secondary, ...otherProps } = this.props;
    const children = this.getChildren();
    switch (elementType) {
      case 'a':
        return <a {...otherProps} className={this.getClassName()} disabled={disabled}>{children}</a>;
      case 'button':
        return <button {...otherProps} className={this.getClassName()} disabled={disabled}>{children}</button>;
      case 'div':
        return <div {...otherProps} className={this.getClassName()} disabled={disabled}>{children}</div>;
      case 'input':
        return <input {...otherProps} className={this.getClassName()} value={children} disabled={disabled} />;
    }
  }
}

/**
 * @prop {propTypes} propTypes attributes for the Button component
 */
Button.propTypes = {
  disabled: React.PropTypes.bool,
  elementType: React.PropTypes.oneOf(['a', 'button', 'div', 'input']).isRequired,
  onClick: React.PropTypes.func.isRequired,
  secondary: React.PropTypes.bool,
  tertiary: React.PropTypes.bool
};

Button.defaultProps = {
  disabled: false,
  elementType: 'button',
  secondary: false,
  tertiary: false
}

export default Button;



// WEBPACK FOOTER //
// ./src/components/clarity-ui/inputs/button.jsx